import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';
import KassirSidebar from '../../components/KassirSidebar';
import AdminSidebar from '../../components/AdminSidebar';
import { fmtTime } from '../../utils/dateFormat';
import { useQrOrders } from '../../context/QrOrdersContext';
import { canFiscalRelay, fiscalForward } from '../../utils/printService';
import { offlineEnabled, getOfflineWaiterList } from '../../utils/offlineCache';
import { getOfflineTableIds, getOfflineDraft, onQueueChange, getOfflinePaidOrderIds } from '../../utils/offlineQueue';
import { isOnline as netIsOnline } from '../../utils/netStatus';
import FloorPlanView, { isPlaced, readableText } from '../../components/FloorPlanView';

// Offline-da bağlanmış (növbədə ödəniş gözləyən) sifarişin masası → boş göstər
const hideOfflinePaid = (list) => {
    const paid = getOfflinePaidOrderIds();
    if (!paid.size) return list;
    return (list || []).map((t) => (t.order_id && paid.has(String(t.order_id))
        ? { ...t, order_id: null, status: 'bos', occupancy_status: 'bos', waiter_user_id: null, waiter_name: null, total: 0, item_count: 0 }
        : t));
};


// Zal planında masanın rəngi (statusa görə)
const PLAN_TONES = {
    bos: { fill: '#dcfce7', stroke: '#22c55e', color: '#166534' },
    dolu: { fill: '#fef3c7', stroke: '#f59e0b', color: '#92400e' },
    hesab: { fill: '#fee2e2', stroke: '#ef4444', color: '#991b1b' },
    offline: { fill: '#fde68a', stroke: '#d97706', color: '#92400e' },
    locked: { fill: '#f3f4f6', stroke: '#d1d5db', color: '#6b7280' },
};

const VIEW_KEY = 'waiter_tables_view';
const tableRoomKey = (table) => (table.room_id ? String(table.room_id) : 'none');

const WaiterTables = () => {
    const [tables, setTables] = useState([]);
    const [floorPlan, setFloorPlan] = useState(false); // superadmin «Zal planı» tick-i (PRO)
    const [floorShapes, setFloorShapes] = useState([]); // planın dekor formaları
    const [tablesView, setTablesView] = useState(() => {
        try { return localStorage.getItem(VIEW_KEY) || 'plan'; } catch { return 'plan'; }
    });
    const [rooms, setRooms] = useState([]);
    // Seçilmiş bölmə (otaq) — masa bağlayıb geri qayıdanda eyni bölmədə qalsın
    const [activeRoom, setActiveRoom] = useState(() => sessionStorage.getItem('waiter_active_room') || 'all');
    const [waiterFilter, setWaiterFilter] = useState('all'); // masaları ofisiant üzrə süz (hamı üçün)
    const [loading, setLoading] = useState(true);
    const [time, setTime] = useState(new Date());
    const [cashboxStatus, setCashboxStatus] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [syncDone, setSyncDone] = useState(false);
    // Waiter picker (admin/kassir opening a new table)
    const [waiterModal,    setWaiterModal]    = useState(false);
    const [waiterList,     setWaiterList]     = useState([]);
    const [waiterLoading,  setWaiterLoading]  = useState(false);
    const [pendingTableId, setPendingTableId] = useState(null);
    const [waiterSearch,   setWaiterSearch]   = useState('');
    const { user, logout } = useAuthStore();
    const navigate = useNavigate();
    // QR orders — global context handles polling, chime, and modal
    const { qrCount, openModal: openQrModal } = useQrOrders() || {};
    const [sidebarOpen, setSidebarOpen] = useState(false);
    // Ofisiantın öz cari qazancı/qalığı (yalnız real ofisiant üçün dolur)
    const [myEarnings, setMyEarnings] = useState(null);

    // Offline draft/növbə olan masalar → narıncı göstərici (additive, yalnız offline flag ilə)
    const [offlineIds, setOfflineIds] = useState(() => getOfflineTableIds());
    useEffect(() => {
        const refresh = () => { setOfflineIds(getOfflineTableIds()); setTables((prev) => hideOfflinePaid(prev)); };
        refresh();
        const off = onQueueChange(refresh);
        window.addEventListener('focus', refresh);
        window.addEventListener('storage', refresh);
        return () => { off(); window.removeEventListener('focus', refresh); window.removeEventListener('storage', refresh); };
    }, []);
    const showOfflineMarks = offlineEnabled();

    const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
    const isKassir = user?.role === 'kassir';
    const isManager = isAdmin || isKassir || !!user?.is_manager || user?.mode === 'simple_kassir'; // includes admin/kassir who logged in via PIN, and simple_kassir
    const canManageCashbox = isAdmin || isKassir;
    // Adi ofisiant (PIN menecer / kassir deyil)
    const isRealWaiter = user?.role === 'waiter' && !user?.is_manager && user?.mode !== 'simple_kassir';

    // ── Zal ekranından Fiskal Kassa (superadmin tick-lərinə görə) ──────────────
    const [cashboxModal,         setCashboxModal]         = useState(false);
    const [cashboxLoading,       setCashboxLoading]       = useState(false);
    const [cashboxActionLoading, setCashboxActionLoading] = useState('');
    // Fiskal kassa aktivdir + rol icazəsi: menecer → waiter_admin tick, ofisiant → waiter_ofisiant tick
    const canWaiterFiscal = Boolean(cashboxStatus?.enabled) && (
        (isManager && cashboxStatus?.waiter_admin) ||
        (isRealWaiter && cashboxStatus?.waiter_ofisiant)
    );

    useEffect(() => {
        const timer = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Öz qazancını çək (admin/kassir/administrator üçün endpoint has:false qaytarır)
    useEffect(() => {
        let active = true;
        api.get('/staff/my-earnings')
            .then((res) => { if (active && res.data?.has) setMyEarnings(res.data); })
            .catch(() => {});
        return () => { active = false; };
    }, []);

    // Seçilmiş bölməni yadda saxla (masa bağlayıb geri qayıdanda bərpa olunsun)
    useEffect(() => { sessionStorage.setItem('waiter_active_room', activeRoom); }, [activeRoom]);
    // Zal planı açıqdırsa otağı olmayan və ya otağı passiv olan masalar da öz bölməsində görünsün
    const roomTabs = useMemo(() => {
        if (!floorPlan) return rooms;
        const list = [...rooms];
        tables.forEach((table) => {
            const id = tableRoomKey(table);
            if (!list.some((r) => String(r.id) === id)) list.push({ id, name: table.room_name || 'Otaqsız' });
        });
        return list;
    }, [rooms, tables, floorPlan]);
    // Otaqlar yüklənəndə: saxlanmış otaq artıq yoxdursa "hamısı"na qayıt
    useEffect(() => {
        if (activeRoom !== 'all' && roomTabs.length && !roomTabs.some(r => String(r.id) === activeRoom)) {
            setActiveRoom('all');
        }
    }, [roomTabs, activeRoom]);

    const loadCashboxStatus = useCallback(async () => {
        if (!canManageCashbox && user?.role !== 'waiter') return;
        try {
            const res = await api.get('/cashbox/status');
            setCashboxStatus(res.data?.data || null);
        } catch {
            setCashboxStatus(null);
        }
    }, [canManageCashbox, user?.role]);

    const openCashboxModal = async () => {
        setCashboxModal(true);
        setCashboxLoading(true);
        await loadCashboxStatus();
        setCashboxLoading(false);
    };

    const runCashboxAction = async (endpoint, successMessage) => {
        setCashboxActionLoading(endpoint);
        try {
            if (canFiscalRelay()) {
                const prep = await api.post('/cashbox/prepare', { action: endpoint });
                const fwd = await fiscalForward(prep.data.relay);
                const done = await api.post('/cashbox/complete', { action: endpoint, raw: fwd.raw, status: fwd.status });
                toast.success(done.data?.msg || successMessage);
            } else {
                await api.post(`/cashbox/${endpoint}`);
                toast.success(successMessage);
            }
            await loadCashboxStatus();
        } catch (err) {
            toast.error(err.response?.data?.msg || err.message || 'Xəta baş verdi.');
        } finally { setCashboxActionLoading(''); }
    };


    const loadData = useCallback(async () => {
        try {
            const requests = [api.get('/tables'), api.get('/rooms')];
            if (canManageCashbox || user?.role === 'waiter') {
                requests.push(api.get('/cashbox/status').catch(() => ({ data: { data: null } })));
            }

            const [tabRes, roomRes, cashboxRes] = await Promise.all(requests);
            const tabs = hideOfflinePaid(tabRes.data.data || []);
            setTables(tabs);
            setFloorPlan(!!tabRes.data.floor_plan);
            setFloorShapes(tabRes.data.floor_shapes || []);
            // Offline snapshot: online ikən açıq sifarişlərin detalını + ofisiant siyahısını keşə yığ
            if (offlineEnabled() && netIsOnline()) {
                const openIds = [...new Set(tabs.filter((t) => t.order_id).map((t) => t.order_id))];
                openIds.forEach((oid) => { api.get(`/orders/${oid}`).catch(() => {}); });
                api.get('/auth/waiters').catch(() => {}); // masa açanda ofisiant picker offline işləsin
                const wt = (() => { try { return localStorage.getItem('pos_waiter_token') || ''; } catch { return ''; } })();
                if (wt) api.get(`/auth/offline-waiters?token=${encodeURIComponent(wt)}`).catch(() => {});
            }
            setRooms(roomRes.data.data || []);
            if (cashboxRes) {
                setCashboxStatus(cashboxRes.data?.data || null);
            }
        } catch {
            toast.error('Masalar yüklənmədi.');
        } finally {
            setLoading(false);
        }
    }, [canManageCashbox, user?.role]);

    // Tables refresh every 30s
    useEffect(() => {
        loadData();
        const interval = setInterval(loadData, 30000);
        // Offline sinxron bitəndə dərhal yenilə (masa statusu stale qalmasın)
        const onSynced = () => loadData();
        window.addEventListener('offline-synced', onSynced);
        window.addEventListener('app-resync', onSynced); // online ⇄ offline keçidi (netMonitor / OfflineBanner)
        return () => { clearInterval(interval); window.removeEventListener('offline-synced', onSynced); window.removeEventListener('app-resync', onSynced); };
    }, [loadData]);


    const shiftRequiredAndClosed = Boolean(cashboxStatus?.enabled) && !cashboxStatus?.shift_open;

    const openWaiterPicker = async (tableId) => {
        let list = [];
        try {
            const res = await api.get('/auth/waiters');
            list = res.data.data || [];
        } catch {
            list = []; // offline/şəbəkə xətası → aşağıda keşlənmiş offline siyahıdan götürülür
        }
        // Boş gəlibsə (offline + keş yox) → offline-waiters keşindən ofisiantları götür
        if (!list.length) {
            const off = getOfflineWaiterList();
            if (off.length) list = off;
        }
        if (list.length === 0) {
            // Ofisiant tapılmadı — cari admin/kassir açan kimi
            navigate(`/waiter/order/new/${tableId}?waiter=${user.id}`);
            return;
        }
        if (list.length === 1) {
            navigate(`/waiter/order/new/${tableId}?waiter=${list[0].id}`);
            return;
        }
        setPendingTableId(tableId);
        setWaiterList(list);
        setWaiterModal(true);
    };

    const handleSelectWaiter = (waiterId) => {
        setWaiterModal(false);
        navigate(`/waiter/order/new/${pendingTableId}?waiter=${waiterId}`);
    };

    const handleTableClick = (table) => {
        // Offline draft olan masa → birbaşa həmin draft-a qayıt (ofisiant picker-siz, draft-dakı ofisiantla)
        if (showOfflineMarks && offlineIds.has(String(table.id)) && !table.order_id) {
            const d = getOfflineDraft(table.id);
            const w = d?.waiter_id || user?.id || '';
            navigate(`/waiter/order/new/${table.id}${w ? `?waiter=${w}` : ''}`);
            return;
        }
        if (table.order_id) {
            if (isManager) {
                navigate(`/waiter/order/${table.order_id}`);
                return;
            }

            const ownsOrder = table.waiter_user_id != null &&
                Number(table.waiter_user_id) === Number(user?.id);
            if (ownsOrder) {
                navigate(`/waiter/order/${table.order_id}`);
                return;
            }

            toast.error(`Bu masa "${table.waiter_name}" tərəfindən istifadə olunur.`);
            return;
        }

        if (shiftRequiredAndClosed) {
            toast.error('Kassa növbəsi hələ açılmayıb.');
            return;
        }

        if (isManager) {
            // Admin/kassir (password or PIN) picks a waiter if 2+ exist
            openWaiterPicker(table.id);
            return;
        }

        navigate(`/waiter/order/new/${table.id}`);
    };


    const statusConfig = {
        bos: {
            label: 'Boş',
            dot: 'bg-green-500',
            card: 'border-green-300 bg-green-50/30',
            badge: 'bg-green-100 text-green-700',
            action: '+ Sifariş aç',
            actionClass: 'bg-green-100 text-green-700 hover:bg-green-500 hover:text-white'
        },
        dolu: {
            label: 'Dolu',
            dot: 'bg-amber-500',
            card: 'border-amber-300 bg-amber-50/30',
            badge: 'bg-amber-100 text-amber-700',
            action: '📋 Sifarişə bax',
            actionClass: 'bg-primary-100 text-primary-700 hover:bg-primary-500 hover:text-white'
        },
        hesab: {
            label: 'Hesab',
            dot: 'bg-red-500',
            card: 'border-red-300 bg-red-50/30',
            badge: 'bg-red-100 text-red-700',
            action: isKassir ? '💳 Hesaba keç' : '💳 Hesabı bağla',
            actionClass: 'bg-red-100 text-red-600 hover:bg-red-500 hover:text-white'
        }
    };

    const days = ['Bazar', 'Bazar ertəsi', 'Çərşənbə axşamı', 'Çərşənbə', 'Cümə axşamı', 'Cümə', 'Şənbə'];
    const months = ['Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'İyun', 'İyul', 'Avqust', 'Sentyabr', 'Oktyabr', 'Noyabr', 'Dekabr'];
    const dateStr = `${days[time.getDay()]}, ${time.getDate()} ${months[time.getMonth()]}`;
    const timeStr = fmtTime(time);

    const bosTables = tables.filter((table) => (table.occupancy_status || 'bos') === 'bos').length;
    const doluTables = tables.filter((table) => (table.occupancy_status || 'bos') === 'dolu').length;
    const hesabTables = tables.filter((table) => (table.occupancy_status || 'bos') === 'hesab').length;

    // Aktiv ofisiantlar (dolu masası olanlar) — filtr seçimləri buradan qurulur (əlavə API lazım deyil)
    const activeWaiters = (() => {
        const m = new Map();
        for (const t of tables) {
            if (t.order_id && t.waiter_user_id != null && !m.has(String(t.waiter_user_id))) {
                m.set(String(t.waiter_user_id), { id: String(t.waiter_user_id), name: t.waiter_name || `#${t.waiter_user_id}` });
            }
        }
        return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
    })();

    // Ofisiant filtri əvvəl tətbiq olunur (seçilibsə → yalnız onun dolu masaları),
    // SONRA otaq filtri — ikisi BİRLƏŞİR (otaq tabları da bu süzülmüş dəstə üzərində qurulur).
    const waiterFilteredTables = waiterFilter === 'all'
        ? tables
        : tables.filter((table) => table.order_id && String(table.waiter_user_id) === waiterFilter);
    const visibleTables = activeRoom === 'all'
        ? waiterFilteredTables.filter((table) => Number(table.is_takeaway || 0) === 0)
        : waiterFilteredTables.filter((table) => tableRoomKey(table) === activeRoom);

    // Zal planı: otaq seçiləndə (otaq yoxdursa hamısında). Plana qoyulmayan masalar aşağıda kart kimi qalır.
    const placedTables = visibleTables.filter(isPlaced);
    const hasPlan = floorPlan && (activeRoom !== 'all' || roomTabs.length <= 1) && placedTables.length > 0;
    // «Hamısı» tabında masalar əvəzinə otaqların adları çıxır
    const showRoomOverview = floorPlan && activeRoom === 'all' && waiterFilter === 'all' && roomTabs.length > 1;
    const overviewRooms = roomTabs
        .map((room) => {
            const roomTables = tables.filter((table) => tableRoomKey(table) === String(room.id));
            return { ...room, count: roomTables.length, occupied: roomTables.filter((table) => table.order_id).length };
        })
        .filter((room) => !isRealWaiter || room.count > 0);
    const showPlan = hasPlan && tablesView === 'plan';
    const gridTables = showPlan ? visibleTables.filter((table) => !isPlaced(table)) : visibleTables;

    const roomShapes = floorShapes.filter((shape) => !Number(shape.fp_overview));
    const planDecor = activeRoom === 'all' ? roomShapes : roomShapes.filter((shape) => tableRoomKey(shape) === activeRoom);

    // Ümumi plan («Hamısı»): admin zalları düymə kimi yerləşdirir, planda olmayan zallar kart qalır
    const overviewDecor = floorShapes.filter((shape) => Number(shape.fp_overview) && !shape.link_room);
    const overviewItems = overviewRooms.flatMap((room) => {
        const tile = floorShapes.find((shape) => shape.link_room === String(room.id) && shape.fp_x !== null);
        if (!tile) return [];
        return [{
            table: { ...tile, id: `r${room.id}`, name: tile.label || room.name },
            fill: tile.fill,
            stroke: 'rgba(0,0,0,.25)',
            color: readableText(tile.fill),
            sub: room.occupied > 0 ? `${room.count} masa · ${room.occupied} dolu` : `${room.count} masa`,
        }];
    });
    const overviewCards = overviewRooms.filter((room) => !overviewItems.some((it) => it.table.id === `r${room.id}`));

    const changeTablesView = (view) => {
        setTablesView(view);
        try { localStorage.setItem(VIEW_KEY, view); } catch { /* ignore */ }
    };

    const planItems = placedTables.map((table) => {
        const status = table.occupancy_status || 'bos';
        const isBusy = !!table.order_id && table.waiter_user_id != null;
        const isLocked = isBusy && Number(table.waiter_user_id) !== Number(user?.id) && !isManager;
        const hasOffline = showOfflineMarks && offlineIds.has(String(table.id));
        const tone = isLocked ? PLAN_TONES.locked : hasOffline ? PLAN_TONES.offline : (PLAN_TONES[status] || PLAN_TONES.bos);
        return {
            table,
            ...tone,
            sub: isLocked ? table.waiter_name : table.order_id ? `${Number(table.order_grand_total ?? table.order_total ?? 0).toFixed(2)} ₼` : `${table.capacity || 0} nəfər`,
            disabled: isLocked,
            title: isLocked ? `Bu masa "${table.waiter_name}" tərəfindən istifadə olunur` : (table.waiter_name || undefined),
        };
    });

    // Plan ekrana sığsın: hündürlük monitorun qalan yerinə görə, zal seçiləndə plan başlığın altına sürüşür
    const showingPlan = showPlan || (showRoomOverview && overviewItems.length > 0);
    const navRef = useRef(null);
    const tabsRef = useRef(null);
    const planRef = useRef(null);
    const [planFit, setPlanFit] = useState({ navH: 0, maxHeight: undefined });

    useEffect(() => {
        const measure = () => {
            const navH = navRef.current?.offsetHeight || 0;
            const tabsH = tabsRef.current?.offsetHeight || 0;
            setPlanFit({ navH, maxHeight: Math.max(360, window.innerHeight - navH - tabsH - 24) });
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [floorPlan, roomTabs.length]);

    useEffect(() => {
        if (!showingPlan) return undefined;
        const raf = requestAnimationFrame(() => {
            const plan = planRef.current;
            if (!plan) return;
            const offset = (navRef.current?.offsetHeight || 0) + (tabsRef.current?.offsetHeight || 0) + 8;
            window.scrollTo({ top: plan.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
        });
        return () => cancelAnimationFrame(raf);
    }, [activeRoom, showingPlan]);

    const getRoleLabel = (role) => ({
        admin: 'Admin',
        superadmin: 'Super Admin',
        kassir: 'Kassir',
        administrator: 'Administrator',
        anbardar: 'Anbardar',
        waiter: 'Ofisiant'
    }[role] || role);

    const getRoleBadge = (role) => ({
        admin: 'bg-primary-100 text-primary-700',
        superadmin: 'bg-purple-100 text-purple-700',
        kassir: 'bg-violet-100 text-violet-700',
        administrator: 'bg-sky-100 text-sky-700',
        anbardar: 'bg-amber-100 text-amber-700',
        waiter: 'bg-green-100 text-green-700'
    }[role] || 'bg-gray-100 text-gray-600');

    // Başlıqda əsl rol göstərilsin (administrator/kassir/admin waiter görünüşünə düşsə də)
    const displayRole = user?.display_role || user?.role;

    return (
        <div className={`min-h-screen bg-gray-50 ${(isKassir || isAdmin) ? 'md:pl-64' : ''}`}>
            {/* Left sidebar */}
            {isKassir && <KassirSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />}
            {isAdmin  && <AdminSidebar  open={sidebarOpen} onClose={() => setSidebarOpen(false)} />}

            <nav ref={navRef} className="sticky top-0 z-30 border-b border-gray-200 bg-white">
                <div className="mx-auto max-w-7xl px-3 py-2 sm:px-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                            {/* Mobile hamburger — only for roles with a sidebar */}
                            {(isAdmin || isKassir) && (
                                <button
                                    onClick={() => setSidebarOpen(true)}
                                    className="md:hidden flex items-center justify-center w-8 h-8 rounded-xl bg-slate-100 text-slate-600 text-base shrink-0"
                                    aria-label="Menyu aç"
                                >
                                    ☰
                                </button>
                            )}
                            {!isAdmin && !isKassir && (
                                <>
                                    <img
                                        src="/logos/fiscal_blue.png"
                                        alt="Logo"
                                        className="h-6 sm:h-7"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                    <span className="text-sm font-bold text-primary-600 sm:text-base">Fiscal POS</span>
                                    <span className="hidden text-xs text-gray-400 sm:block">·</span>
                                </>
                            )}
                            <span className="hidden text-xs text-gray-500 sm:block">{dateStr}</span>
                        </div>

                        <div className="flex flex-wrap items-center justify-end gap-2">
                            <span className="hidden font-mono text-sm text-gray-500 sm:block">{timeStr}</span>
                            {/* Fiskal Kassa — Zal ekranından idarə (superadmin tick: admin / ofisiant) */}
                            {canWaiterFiscal && (
                                <button
                                    type="button"
                                    onClick={openCashboxModal}
                                    className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-bold transition-all sm:px-3 sm:text-xs ${
                                        cashboxStatus?.shift_open
                                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-500 hover:text-white'
                                            : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-500 hover:text-white'
                                    }`}
                                >
                                    💳
                                    <span className="hidden sm:inline">{cashboxStatus?.shift_open ? 'Kassa: Açıq' : 'Kassa'}</span>
                                </button>
                            )}
                            {/* QR button — only for waiter role; kassir+admin have it in sidebar */}
                            {!isAdmin && !isKassir && (
                                <button
                                    type="button"
                                    onClick={openQrModal}
                                    className="relative flex items-center gap-1.5 rounded-lg bg-orange-50 border border-orange-200 px-2.5 py-1.5 text-[11px] font-bold text-orange-600 transition-all hover:bg-orange-500 hover:text-white sm:px-3 sm:text-xs"
                                >
                                    📲
                                    <span className="hidden sm:inline">QR</span>
                                    {qrCount > 0 && (
                                        <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 animate-pulse items-center justify-center rounded-full bg-red-500 text-[9px] font-black text-white">
                                            {qrCount}
                                        </span>
                                    )}
                                </button>
                            )}
                            <button
                                onClick={async () => {
                                    setSyncing(true);
                                    setSyncDone(false);
                                    await loadData();
                                    setSyncing(false);
                                    setSyncDone(true);
                                    setTimeout(() => setSyncDone(false), 2000);
                                }}
                                disabled={syncing}
                                className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition-all sm:text-xs ${
                                    syncDone
                                        ? 'bg-green-100 text-green-600'
                                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                } disabled:opacity-60`}
                            >
                                <span className={syncing ? 'animate-spin inline-block' : ''}>🔄</span>
                                <span className="hidden sm:inline">
                                    {syncing ? 'Yüklənir...' : syncDone ? 'Sinxronlaşdırıldı ✓' : 'Sinxronlaşdır'}
                                </span>
                            </button>
                            {/* User info + logout — only for waiter (sidebar roles have their own) */}
                            {!isAdmin && !isKassir && (
                                <>
                                    <span className="max-w-[120px] truncate text-[11px] text-gray-600 sm:max-w-none sm:text-xs">👤 {user?.name}</span>
                                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${getRoleBadge(displayRole)}`}>
                                        {user?.job_title?.trim() ? user.job_title : getRoleLabel(displayRole)}
                                    </span>
                                    {myEarnings && (
                                        <span
                                            className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700"
                                            title={myEarnings.payment_type === 'service_pct'
                                                ? `Servis haqqı: qazanılan ${Number(myEarnings.earned).toFixed(2)} ₼ · ödənilən ${Number(myEarnings.paid).toFixed(2)} ₼`
                                                : `Aylıq maaş ${Number(myEarnings.monthly_salary).toFixed(2)} ₼ · ödənilən ${Number(myEarnings.paid).toFixed(2)} ₼`}
                                        >
                                            💵 {myEarnings.payment_type === 'service_pct' ? 'Servis haqqı' : 'Qalıq maaş'}: {Number(myEarnings.remaining).toFixed(2)} ₼
                                        </span>
                                    )}
                                    <button
                                        onClick={() => logout(navigate)}
                                        className="rounded-lg border border-red-100 px-2.5 py-1.5 text-[11px] font-semibold text-red-500 transition-all hover:bg-red-500 hover:text-white sm:px-3 sm:text-xs"
                                    >
                                        Çıxış
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            <div className="mx-auto max-w-7xl px-3 py-4 sm:px-4 sm:py-6">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-gray-800">Zal</h1>
                        <p className="mt-0.5 text-xs text-gray-400">{user?.tenant_name}</p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-[11px] sm:gap-4 sm:text-xs">
                        {/* Ofisiant filtri — YALNIZ admin/kassir/administrator. Adi ofisiant görməsin. */}
                        {isManager && (activeWaiters.length > 0 || waiterFilter !== 'all') && (
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-400">👤 Ofisiant:</span>
                                <select
                                    value={waiterFilter}
                                    onChange={(e) => { setWaiterFilter(e.target.value); setActiveRoom('all'); }}
                                    className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 focus:border-primary-400 focus:outline-none"
                                >
                                    <option value="all">Bütün ofisiantlar</option>
                                    {activeWaiters.map((w) => (
                                        <option key={w.id} value={w.id}>{w.name}</option>
                                    ))}
                                </select>
                                {waiterFilter !== 'all' && (
                                    <button
                                        onClick={() => setWaiterFilter('all')}
                                        className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-bold text-gray-500 hover:bg-gray-50"
                                    >
                                        ✕ Filtri sıfırla
                                    </button>
                                )}
                            </div>
                        )}
                        {hasPlan && (
                            <div className="flex gap-1 rounded-xl border border-gray-200 bg-white p-1">
                                {[['plan', '🗺️ Plan'], ['cards', '▦ Kartlar']].map(([key, label]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => changeTablesView(key)}
                                        className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${tablesView === key ? 'bg-primary-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        )}
                        <span className="flex items-center gap-1.5 text-gray-500">
                            <span className="inline-block h-2.5 w-2.5 rounded-full bg-green-500" />
                            Boş ({bosTables})
                        </span>
                        <span className="flex items-center gap-1.5 text-gray-500">
                            <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" />
                            Dolu ({doluTables})
                        </span>
                        <span className="flex items-center gap-1.5 text-gray-500">
                            <span className="inline-block h-2.5 w-2.5 rounded-full bg-red-500" />
                            Hesab ({hesabTables})
                        </span>
                        {cashboxStatus?.enabled && (
                            <span className={`rounded-full px-2.5 py-1 font-bold ${cashboxStatus.shift_open ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                                {cashboxStatus.shift_open ? 'Kassa növbəsi açıqdır' : 'Kassa növbəsi açılmayıb'}
                            </span>
                        )}
                    </div>
                </div>

                {roomTabs.length > 0 && (
                    <div
                        ref={tabsRef}
                        className={`mb-3 flex gap-2 overflow-x-auto py-1 ${floorPlan ? 'sticky z-20 bg-gray-50' : ''}`}
                        style={floorPlan ? { top: planFit.navH } : undefined}
                    >
                        <button
                            onClick={() => setActiveRoom('all')}
                            className={`flex-shrink-0 whitespace-nowrap rounded-xl px-4 py-2 text-sm font-bold transition-all ${
                                activeRoom === 'all'
                                    ? 'text-white shadow-md'
                                    : 'border border-gray-200 bg-white text-gray-500 hover:border-primary-300'
                            }`}
                            style={activeRoom === 'all' ? { background: 'linear-gradient(135deg, #006f91, #0090bb)' } : {}}
                        >
                            Hamısı
                            <span className="ml-1.5 text-xs opacity-80">({waiterFilter === 'all' ? tables.length : waiterFilteredTables.filter((t) => Number(t.is_takeaway || 0) === 0).length})</span>
                        </button>

                        {roomTabs.map((room) => {
                            const roomTables = waiterFilteredTables.filter((table) => tableRoomKey(table) === String(room.id));
                            const count = roomTables.length;
                            const occupied = roomTables.filter((table) => table.order_id).length;
                            // Ofisiant filtri aktivdirsə və bu otaqda onun masası yoxdursa → tabı gizlət
                            if (waiterFilter !== 'all' && count === 0) return null;

                            return (
                                <button
                                    key={room.id}
                                    onClick={() => setActiveRoom(String(room.id))}
                                    className={`relative flex-shrink-0 whitespace-nowrap rounded-xl px-4 py-2 text-sm font-bold transition-all ${
                                        activeRoom === String(room.id)
                                            ? 'text-white shadow-md'
                                            : 'border border-gray-200 bg-white text-gray-600 hover:border-primary-300'
                                    }`}
                                    style={activeRoom === String(room.id) ? { background: 'linear-gradient(135deg, #006f91, #0090bb)' } : {}}
                                >
                                    {room.name}
                                    <span className="ml-1.5 text-xs opacity-80">({count})</span>
                                    {occupied > 0 && (
                                        <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[9px] font-black text-white">
                                            {occupied}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}

                {loading ? (
                    <div className="flex h-64 items-center justify-center text-gray-400">Yüklənir...</div>
                ) : showRoomOverview ? (
                    <>
                    {overviewItems.length > 0 && (
                        <div className="mb-4" ref={planRef}>
                            <FloorPlanView items={overviewItems} decor={overviewDecor} maxHeight={planFit.maxHeight} onOpen={(tile) => setActiveRoom(String(tile.link_room))} />
                        </div>
                    )}
                    <div className={`flex flex-wrap items-center justify-center gap-4 ${overviewItems.length ? '' : 'min-h-[50vh] content-center'}`}>
                        {overviewCards.map((room) => (
                            <button
                                key={room.id}
                                type="button"
                                onClick={() => setActiveRoom(String(room.id))}
                                className="w-52 rounded-2xl border-2 border-primary-200 bg-white px-5 py-8 text-center transition-all hover:-translate-y-0.5 hover:border-primary-400 hover:shadow-md"
                            >
                                <div className="truncate text-xl font-black text-gray-800">{room.name}</div>
                                <div className="mt-2 text-sm text-gray-500">{room.count} masa</div>
                                {room.count > 0 && (
                                    <div className={`mt-1 text-xs font-bold ${room.occupied > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                        {room.occupied > 0 ? `${room.occupied} dolu` : 'Hamısı boş'}
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                    </>
                ) : visibleTables.length === 0 ? (
                    <div className="flex h-64 flex-col items-center justify-center gap-2 text-gray-400">
                        <span className="text-4xl">🍽️</span>
                        <p>{waiterFilter !== 'all' ? 'Bu ofisiantın açıq masası yoxdur.' : 'Bu otaqda masa yoxdur.'}</p>
                    </div>
                ) : (
                    <>
                    {showPlan && (
                        <div className="mb-4" ref={planRef}>
                            <FloorPlanView items={planItems} decor={planDecor} maxHeight={planFit.maxHeight} onOpen={handleTableClick} />
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                        {gridTables.map((table) => {
                            const realStatus = table.occupancy_status || (Number(table.item_count || 0) > 0
                                ? (table.status === 'hesab' ? 'hesab' : 'dolu')
                                : 'bos');
                            const config = statusConfig[realStatus] || statusConfig.bos;
                            const isTakeawayTable = Number(table.is_takeaway || 0) === 1;

                            // Başqa ofisiantın açıq sifarişi varsa → bağlı (greyed) göstər
                            // is_manager olanlar bütün masaları aça bilər
                            const isBusy   = !!table.order_id && table.waiter_user_id != null;
                            const isOwn    = isBusy && Number(table.waiter_user_id) === Number(user?.id);
                            const isLocked = isBusy && !isOwn && !isManager;
                            // Offline draft/növbə var? → narıncı göstərici (serverdə hələ yoxdur)
                            const hasOffline = showOfflineMarks && offlineIds.has(String(table.id));

                            return (
                                <button
                                    key={table.id}
                                    type="button"
                                    onClick={() => handleTableClick(table)}
                                    disabled={isLocked}
                                    title={isLocked ? `Bu masa "${table.waiter_name}" tərəfindən istifadə olunur` : (hasOffline ? 'Offline sifariş — internet qayıdanda sinxronlaşacaq' : undefined)}
                                    className={`rounded-2xl border-2 p-4 text-left transition-all ${
                                        isLocked
                                            ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed grayscale'
                                            : 'hover:-translate-y-0.5 hover:shadow-md ' + (hasOffline
                                                ? 'border-amber-400 bg-amber-50/60'
                                                : isTakeawayTable
                                                    ? 'border-orange-300 bg-orange-50/40'
                                                    : config.card)
                                    }`}
                                >
                                    <div className="mb-2 flex items-start justify-between gap-2">
                                        <div>
                                            <div className="text-xs font-semibold text-gray-400">
                                                {isTakeawayTable ? '🥡 Takeaway' : `№ ${table.number}`}
                                            </div>
                                            <div className="text-lg font-black text-gray-800 flex items-center gap-1">
                                                {table.name}
                                                {isLocked && <span title="Kilidlənmiş">🔒</span>}
                                            </div>
                                        </div>
                                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${
                                            isLocked ? 'bg-gray-200 text-gray-500'
                                                : hasOffline ? 'bg-amber-200 text-amber-800'
                                                    : isTakeawayTable ? 'bg-orange-100 text-orange-700' : config.badge
                                        }`}>
                                            {isLocked ? 'Başqa ofisiant' : hasOffline ? '📴 Offline' : config.label}
                                        </span>
                                    </div>

                                    <div className="space-y-1 text-xs text-gray-500">
                                        {isTakeawayTable
                                            ? <div className="text-orange-500 font-semibold">🥡 Takeaway sifarişi</div>
                                            : <div>{table.room_name || 'Otaqsız'}</div>
                                        }
                                        <div>{table.capacity || 0} nəfər</div>
                                        {table.waiter_name && <div>Ofisiant: {table.waiter_name}</div>}
                                    </div>

                                    <div className={`mt-4 rounded-xl px-3 py-2 text-center text-xs font-bold transition-all ${
                                        hasOffline
                                            ? 'bg-amber-200 text-amber-800 hover:bg-amber-500 hover:text-white'
                                            : isTakeawayTable
                                                ? 'bg-orange-100 text-orange-700 hover:bg-orange-500 hover:text-white'
                                                : config.actionClass
                                    }`}>
                                        {hasOffline ? '📴 Offline sifariş' : config.action}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                    </>
                )}
            </div>

        {/* ── Fiskal Kassa modal (Zal ekranı — superadmin tick ilə) ─── */}
        {cashboxModal && (
            <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                onClick={(e) => e.target === e.currentTarget && setCashboxModal(false)}
            >
                <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl">
                    <div className="border-b border-gray-100 p-5">
                        <h3 className="text-lg font-bold text-gray-800">💳 Fiskal Kassa</h3>
                        <p className="mt-1 text-sm text-gray-400">Shift, hesabat və son qaimə əməliyyatları</p>
                    </div>
                    <div className="space-y-4 p-5">
                        {cashboxLoading ? (
                            <div className="py-10 text-center text-gray-400">Yüklənir...</div>
                        ) : (
                            <>
                                <div className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${
                                    cashboxStatus?.shift_open
                                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                        : 'border-red-200 bg-red-50 text-red-700'
                                }`}>
                                    {cashboxStatus?.enabled
                                        ? (cashboxStatus.shift_open ? 'Kassa növbəsi hazırda açıqdır.' : 'Kassa növbəsi hazırda bağlıdır.')
                                        : 'Bu tenant üçün kassa inteqrasiyası aktiv deyil.'}
                                </div>
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <button type="button"
                                        disabled={!cashboxStatus?.enabled || cashboxStatus?.shift_open || cashboxActionLoading === 'open-shift'}
                                        onClick={() => runCashboxAction('open-shift', 'Kassa növbəsi açıldı.')}
                                        className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700 transition-all hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50">
                                        {cashboxActionLoading === 'open-shift' ? 'Açılır...' : 'Shift aç'}
                                    </button>
                                    <button type="button"
                                        disabled={!cashboxStatus?.enabled || !cashboxStatus?.shift_open || cashboxActionLoading === 'close-shift'}
                                        onClick={() => runCashboxAction('close-shift', 'Kassa növbəsi bağlandı.')}
                                        className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700 transition-all hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50">
                                        {cashboxActionLoading === 'close-shift' ? 'Bağlanır...' : 'Shift bağla'}
                                    </button>
                                    <button type="button"
                                        disabled={!cashboxStatus?.enabled || cashboxActionLoading === 'last-receipt-copy'}
                                        onClick={() => runCashboxAction('last-receipt-copy', 'Sonuncu qaimənin kopyası göndərildi.')}
                                        className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-bold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                                        {cashboxActionLoading === 'last-receipt-copy' ? 'Göndərilir...' : 'Son qaiməni çap et'}
                                    </button>
                                    <button type="button"
                                        disabled={!cashboxStatus?.enabled || cashboxActionLoading === 'x-report'}
                                        onClick={() => runCashboxAction('x-report', 'X hesabatı göndərildi.')}
                                        className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-bold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                                        {cashboxActionLoading === 'x-report' ? 'Göndərilir...' : 'X hesabat'}
                                    </button>
                                    <button type="button"
                                        disabled={!cashboxStatus?.enabled || cashboxActionLoading === 'z-report'}
                                        onClick={() => runCashboxAction('z-report', 'Z hesabatı göndərildi.')}
                                        className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-bold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:col-span-2">
                                        {cashboxActionLoading === 'z-report' ? 'Göndərilir...' : 'Z hesabat'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                    <div className="flex justify-end border-t border-gray-100 p-5">
                        <button type="button" onClick={() => setCashboxModal(false)}
                            className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-500 transition-all hover:bg-gray-50">
                            Bağla
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* ── Waiter picker modal (admin/kassir opening a new table) ─── */}
        {waiterModal && (
            <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                onClick={(e) => e.target === e.currentTarget && setWaiterModal(false)}
            >
                <div className="w-full max-w-sm rounded-2xl bg-white shadow-xl">
                    <div className="border-b border-gray-100 px-5 py-4">
                        <h3 className="text-base font-bold text-gray-800">👤 Ofisiant seçin</h3>
                        <p className="mt-0.5 text-xs text-gray-400">Bu masanı kim idarə edəcək?</p>
                    </div>

                    {waiterList.length > 5 && (
                        <div className="px-4 pt-3 relative">
                            <input
                                type="text"
                                value={waiterSearch}
                                onChange={(e) => setWaiterSearch(e.target.value)}
                                placeholder="🔍 Ofisiant axtar..."
                                className="w-full rounded-xl border border-gray-200 px-3 py-2 pr-9 text-sm focus:outline-none focus:border-blue-400"
                                autoFocus
                            />
                            {waiterSearch && (
                                <button
                                    type="button"
                                    onClick={() => setWaiterSearch('')}
                                    className="absolute right-6 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 text-xs"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    )}

                    <div className="p-4 space-y-2 max-h-80 overflow-y-auto">
                        {waiterLoading ? (
                            <p className="text-center text-sm text-gray-400 py-6">Yüklənir...</p>
                        ) : waiterList.length === 0 ? (
                            <p className="text-center text-sm text-gray-400 py-6">Aktiv ofisiant tapılmadı.</p>
                        ) : (
                            waiterList
                                .filter(w => !waiterSearch.trim() || String(w.name || '').toLowerCase().includes(waiterSearch.toLowerCase().trim()))
                                .map((w) => (
                                <button
                                    key={w.id}
                                    onClick={() => handleSelectWaiter(w.id)}
                                    className="flex w-full items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-left hover:bg-blue-50 hover:border-blue-300 transition-all"
                                >
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 font-bold text-sm">
                                        {w.name?.[0] || '?'}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-semibold text-sm text-slate-800 truncate">{w.name}</p>
                                        <p className="text-xs text-slate-400">Ofisiant</p>
                                    </div>
                                    <span className="ml-auto text-blue-400 text-lg">›</span>
                                </button>
                            ))
                        )}
                    </div>

                    <div className="border-t border-gray-100 px-5 py-3">
                        <button
                            onClick={() => setWaiterModal(false)}
                            className="w-full rounded-xl border border-slate-200 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-50 transition-all"
                        >
                            Ləğv et
                        </button>
                    </div>
                </div>
            </div>
        )}
    </div>
    );
};


export default WaiterTables;

